import React, { useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import Autosuggest from "react-autosuggest";
import throttle from "lodash.throttle";
import chroma from "chroma-js";

import { colors, TextField } from "../theme";

const Container = styled.div`
  position: relative;

  i {
    color: ${colors.green};
    font-size: 1.2em;
    position: absolute;
    right: 10px;
    top: 50%;
    margin-top: -9px;
  }

  ul {
    background-color: white;
    box-shadow: 0 4px 11px rgba(0, 0, 0, 0.1);
    list-style: none;
    margin: 8px 0;
    max-height: 300px;
    overflow-y: auto;
    padding: 4px 0;
    position: absolute;
    width: 100%;
    z-index: 1;
    -webkit-overflow-scrolling: touch;
  }

  li {
    cursor: default;
    padding: 8px 12px;
  }

  .react-autosuggest__suggestion--highlighted {
    background: ${chroma(colors.purple).alpha(0.15)};
  }
`;

const PlaceAutocomplete = ({
  defaultValue,
  onSelection,
  isRequired,
  error,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [suggestionSelected, setSuggestionSelected] = useState();
  const [inputValue, setInputValue] = useState(defaultValue || "");

  const handleSuggestionsFetchRequested = ({ value }) => {
    if (value) {
      setIsFetching(true);
      fetchSuggestions(value);
    }
  };

  const handleSuggestionsClearRequested = () => setSuggestions([]);

  const handleInputChange = (event, { newValue }) => {
    setInputValue(newValue);
  };

  const handleSelection = () => {
    if (onSelection && suggestionSelected) {
      onSelection(suggestionSelected);
    } else if (suggestions[0]) {
      onSelection(suggestions[0]);
    }
  };

  const getSuggestionValue = (suggestion) => {
    setSuggestionSelected(suggestion);
    return suggestion.text;
  };

  const fetchSuggestions = throttle(async (value) => {
    try {
      const res = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${value}.json?access_token=${process.env.RAZZLE_MAPBOX_TOKEN}&types=address&language=fr&proximity=-2.4350132,46.1314128`
      );
      const geodata = (await res.json()).features;

      const suggestions = geodata.map((data) => {
        const suggestion = {
          text: data.place_name_fr,
          number: data.address,
          street: data.text_fr,
        };

        for (const context of data.context) {
          if (/postcode/.test(context.id)) {
            suggestion.zipcode = context.text_fr;
          }
          if (/place/.test(context.id)) {
            suggestion.city = context.text_fr;
          }
          if (/country/.test(context.id)) {
            suggestion.country = context.text_fr;
          }
        }

        return suggestion;
      });
      setSuggestions(suggestions);
      setIsFetching(false);
    } catch (err) {
      setSuggestions([]);
      setIsFetching(false);
    }
  }, 800);

  return (
    <Container>
      <Autosuggest
        alwaysRenderSuggestions={true}
        suggestions={suggestions}
        onSuggestionsFetchRequested={handleSuggestionsFetchRequested}
        onSuggestionsClearRequested={handleSuggestionsClearRequested}
        onSuggestionSelected={handleSelection}
        getSuggestionValue={getSuggestionValue}
        renderSuggestion={(suggestion) => suggestion.text}
        inputProps={{
          value: inputValue,
          onChange: handleInputChange,
        }}
        renderInputComponent={(inputProps) => (
          <TextField
            label="Adresse postale"
            {...inputProps}
            required={isRequired}
            error={error !== undefined}
            helperText={error}
            autoComplete="off"
          />
        )}
      />
      {isFetching && <i className="fas fa-spinner fa-spin" />}
    </Container>
  );
};

PlaceAutocomplete.propTypes = {
  defaultValue: PropTypes.string,
  onSelection: PropTypes.func,
  isRequired: PropTypes.bool,
  error: PropTypes.string,
};

export default PlaceAutocomplete;
